/// <amd-module name="Build/ApprovalsPanel/Utilities" />
import * as Constants from "Build/ApprovalsPanel/Constants";
import * as Resources from "Build/ApprovalsPanel/Resources";
import { ApprovalStatus } from "Build/Client/Contracts/Approval";
import { ApprovalDetails, ApprovalStep, ChecksPermission, TaskCheckDetails } from "Build/Client/Contracts/DataProvider";
import { CheckRunStatus, CheckRunUpdate } from "Build/Client/Contracts/PipelinesChecks";

import { format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IStatusProps, Statuses } from "VSSUI/Status";

export interface IStatusIndicatorData {
    statusProps: IStatusProps;
    label: string;
    detailedLabel?: string;
}

export function getApprovalStatusIndicatorData(approvalStatus: ApprovalStatus, shouldShowResume: boolean = false): IStatusIndicatorData {
    let indicatorData: IStatusIndicatorData = {
        statusProps: Statuses.Waiting,
        label: Resources.WaitingApproval
    };
    switch (approvalStatus) {
        case ApprovalStatus.Rejected:
            indicatorData.statusProps = Statuses.Failed;
            indicatorData.label = Resources.Rejected;
            break;
        case ApprovalStatus.Canceled:
            indicatorData.statusProps = Statuses.Canceled;
            indicatorData.label = Resources.Canceled;
            break;
        case ApprovalStatus.Approved:
            indicatorData.statusProps = Statuses.Success;
            indicatorData.label = shouldShowResume ? Resources.Resumed : Resources.Approved;
            break;
        case ApprovalStatus.Uninitiated:
            indicatorData.statusProps = Statuses.Queued;
            indicatorData.label = Resources.WaitingInSequence;
            break;
        case ApprovalStatus.TimedOut:
            indicatorData.statusProps = Statuses.Queued;
            indicatorData.label = Resources.TimedOut;
            break;
        case ApprovalStatus.Skipped:
            indicatorData.statusProps = Statuses.Skipped;
            indicatorData.label = Resources.Bypassed;
            break;
        case ApprovalStatus.Deferred:
            indicatorData.statusProps = Statuses.Success;
            indicatorData.label = Resources.DeferredApproval;
            break;
    }

    return indicatorData;
}

export function getExclusiveLockStatusIndicatorData(checkRunStatus: CheckRunStatus): IStatusIndicatorData {
    let indicatorData: IStatusIndicatorData = {
        statusProps: Statuses.Waiting,
        label: Resources.Waiting
    };
    switch (checkRunStatus) {
        case CheckRunStatus.Rejected:
        case CheckRunStatus.Canceled:
            indicatorData.statusProps = Statuses.Canceled;
            indicatorData.label = Resources.ExclusiveLockDidNotObtain;
            break;
        case CheckRunStatus.Failed:
            indicatorData.statusProps = Statuses.Failed;
            indicatorData.label = Resources.Failed;
            break;
        case CheckRunStatus.Approved:
            indicatorData.statusProps = Statuses.Success;
            indicatorData.label = Resources.ExclusiveLockAcquired;
            break;
        case CheckRunStatus.Queued:
            indicatorData.statusProps = Statuses.Queued;
            indicatorData.label = Resources.ExclusiveLockQueued;
            break;
        case CheckRunStatus.Running:
            indicatorData.statusProps = Statuses.Waiting;
            indicatorData.label = Resources.ExclusiveLockWaiting;
            break;
        case CheckRunStatus.TimedOut:
            indicatorData.statusProps = Statuses.Queued;
            indicatorData.label = Resources.TimedOut;
            break;
        case CheckRunStatus.Deferred:
            indicatorData.statusProps = Statuses.Success;
            indicatorData.label = Resources.DeferredApproval;
    }

    return indicatorData;
}

export function checksToApprovalStatus(checkRunStatus: CheckRunStatus): ApprovalStatus {

    switch (checkRunStatus) {
        case CheckRunStatus.Rejected:
            return ApprovalStatus.Rejected;

        case CheckRunStatus.Canceled:
            return ApprovalStatus.Canceled;

        case CheckRunStatus.Approved:
            return ApprovalStatus.Approved;

        case CheckRunStatus.Running:
            return ApprovalStatus.Pending;

        case CheckRunStatus.TimedOut:
            return ApprovalStatus.TimedOut;

        case CheckRunStatus.Bypassed:
            return ApprovalStatus.Skipped;

        case CheckRunStatus.None:
            return ApprovalStatus.Undefined;

        case CheckRunStatus.Deferred:
            return ApprovalStatus.Deferred;
    }

    return ApprovalStatus.Uninitiated;
}

export function getApprovalStatus(approvalStatus: ApprovalStatus, shouldShowResume: boolean = false): string {
    let approvalStatusMessage: string = "";
    switch (approvalStatus) {
        case ApprovalStatus.Pending:
            approvalStatusMessage = Resources.Waiting;
            break;
        case ApprovalStatus.Approved:
            approvalStatusMessage = shouldShowResume ? Resources.ResumedFormat : Resources.ApprovedFormat; 
            break;
        case ApprovalStatus.Rejected:
            approvalStatusMessage = Resources.RejectedFormat;
            break;
        case ApprovalStatus.TimedOut:
            approvalStatusMessage = Resources.TimedOutFormat;
            break;
        case ApprovalStatus.Uninitiated:
            approvalStatusMessage = Resources.WaitingInSequence;
            break;
        case ApprovalStatus.Canceled:
            approvalStatusMessage = Resources.Canceled;
            break;
        case ApprovalStatus.Skipped:
            approvalStatusMessage = Resources.Bypassed;
            break;
        case ApprovalStatus.Deferred:
            approvalStatusMessage = Resources.DeferredApproval;
    }
    return approvalStatusMessage;
}

export function getCheckRunStatusIndicatorData(checkRunStatus: CheckRunStatus): IStatusIndicatorData {
    let indicatorData: IStatusIndicatorData = {
        statusProps: Statuses.Waiting,
        label: Resources.Waiting
    };
    switch (checkRunStatus) {
        case CheckRunStatus.Rejected:
            indicatorData.statusProps = Statuses.Failed;
            indicatorData.label = Resources.Failed;
            break;
        case CheckRunStatus.Failed:
            indicatorData.statusProps = Statuses.Failed;
            indicatorData.label = Resources.Failed;
            break;
        case CheckRunStatus.Canceled:
            indicatorData.statusProps = Statuses.Canceled;
            indicatorData.label = Resources.Canceled;
            break;
        case CheckRunStatus.Approved:
            indicatorData.statusProps = Statuses.Success;
            indicatorData.label = Resources.Passed;
            break;
        case CheckRunStatus.Queued:
            indicatorData.statusProps = Statuses.Queued;
            indicatorData.label = Resources.Queued;
            break;
        case CheckRunStatus.Running:
            indicatorData.statusProps = Statuses.Running;
            indicatorData.label = Resources.InProgress;
            break;
        case CheckRunStatus.TimedOut:
            indicatorData.statusProps = Statuses.Queued;
            indicatorData.label = Resources.TimedOut;
            break;
        case CheckRunStatus.Bypassed:
            indicatorData.statusProps = Statuses.Skipped;
            indicatorData.label = Resources.Bypassed;
            break;
        case CheckRunStatus.Deferred:
            indicatorData.statusProps = Statuses.Success;
            indicatorData.label = Resources.DeferredApproval;
    }

    return indicatorData;
}

export function getCheckRunUpdateModifiedByIdentity(checkRunStatus: CheckRunStatus, resultUpdates?: CheckRunUpdate[]): IdentityRef | undefined {
    if (resultUpdates && resultUpdates?.length > 0){
        let latestMatchingResultUpdate = resultUpdates
            .find(ru => ru.status === checkRunStatus);
        if (latestMatchingResultUpdate){
            return latestMatchingResultUpdate.modifiedBy;
        }
    }
}

export function getResourceTypeIcons(resourceType: string): string {

    let resourceIcon = ""
    switch (resourceType) {
        case Constants.Resource_Environment:
            resourceIcon = Environment_IconName;
            break;
        case Constants.Resource_Queue:
            resourceIcon = Queue_IconName;
            break;
        case Constants.Resource_ServiceEndPoint:
            resourceIcon = ServiceEndPoint_IconName;
            break;
        case Constants.Resource_SecureFile:
            resourceIcon = SecureFile_IconName;
            break;
        case Constants.Resource_VariableGroup:
            resourceIcon = VariableGroup_IconName;
            break;
        case Constants.Resource_AgentPool:
            resourceIcon = AgentPool_IconName;
            break;
        case Constants.Resource_Repository:
            resourceIcon = Repository_IconName;
            break;
    }
    return resourceIcon;
}

export function getResourceTypeString(resourceType: string): string {
    let resourceTypeString = "";

    switch (resourceType) {
        case Constants.Resource_Environment:
            resourceTypeString = Resources.Environment;
            break;
        case Constants.Resource_Queue:
            resourceTypeString = Resources.AgentPool;
            break;
        case Constants.Resource_ServiceEndPoint:
            resourceTypeString = Resources.ServiceConnection;
            break;
        case Constants.Resource_SecureFile:
            resourceTypeString = Resources.SecureFile;
            break;
        case Constants.Resource_VariableGroup:
            resourceTypeString = Resources.VariableGroup;
            break;
        case Constants.Resource_AgentPool:
            resourceTypeString = Resources.AgentPool;
            break;
        case Constants.Resource_Repository:
            resourceTypeString = Resources.Repository;
            break;
    }

    return resourceTypeString;
}

export function approvalSort(a: ApprovalDetails, b: ApprovalDetails): number {
    const aValue = getStatusValue(a.status);
    const bValue = getStatusValue(b.status);

    if (aValue < bValue) {
        return -1;
    }
    if (aValue > bValue) {
        return 1;
    }
    return 0;
}

export function stepSort(a: ApprovalStep, b: ApprovalStep) {
    const aValue = a.id;
    const bValue = b.id;

    if (aValue < bValue) {
        return -1;
    }
    if (aValue > bValue) {
        return 1;
    }
    return 0;
}

export function getRemainingMinutesForCheckTimeout(timeout: number, createdDate: Date): number {
    const now = new Date();
    const remainingMinutesForTimeout = timeout * minute - (now.getTime() - createdDate.getTime());

    return remainingMinutesForTimeout;
}

export function getCheckTimeoutOrReevaluationString(scenario: string, remainingMinutesForTimeout: number): string {
    let result = "";

    const days = Math.floor(remainingMinutesForTimeout / day);
    const hours = Math.floor((remainingMinutesForTimeout % day) / hour);
    const minutes = Math.floor((remainingMinutesForTimeout % hour) / minute);

    if (remainingMinutesForTimeout < hour) {
        result = format(Resources.RemainingTimeInMinutes, scenario, minutes);
    }
    else if (remainingMinutesForTimeout < day) {
        result = format(Resources.RemainingTimeInHours, scenario, hours);
    }
    else {
        result = format(Resources.RemainingTimeInDays, scenario, days);
    }

    return result;
}

export function approversSort(a: ApprovalStep, b: ApprovalStep) {
    const aValue = a.assignedApprover.isContainer;
    const bValue = b.assignedApprover.isContainer;

    //Both steps are groups, so we sort based on alphabetical order.
    if (aValue && bValue) {
        return a.assignedApprover.displayName.localeCompare(b.assignedApprover.displayName, navigator.language);
    }

    //Both of them are not groups, so we sort based on alphabetical order.
    if (aValue === undefined && bValue === undefined) {
        return a.assignedApprover.displayName.localeCompare(b.assignedApprover.displayName, navigator.language);
    }

    //If either of them is a group then it should be ordered first
    if (aValue === undefined) {
        return 1;
    }
    if (bValue === undefined) {
        return -1;
    }
    return 0;
}

export function myApprovalsSort(a: ApprovalStep, b: ApprovalStep) {
    const aValue = a.assignedApprover.isContainer;
    const bValue = b.assignedApprover.isContainer;
    if (aValue === undefined) {
        return -1;
    }
    if (bValue === undefined) {
        return 1;
    }
    if (aValue && bValue) {
        return a.assignedApprover.displayName.localeCompare(b.assignedApprover.displayName, navigator.language);
    }
    return 0;
}

export function isInternalTaskCheck(taskCheckDefinition: TaskCheckDetails): boolean {
    if (taskCheckDefinition && taskCheckDefinition.taskCheckConfigSettings && taskCheckDefinition.taskCheckConfigSettings.definitionRef && taskCheckDefinition.taskCheckConfigSettings.definitionRef.id) {
        switch (taskCheckDefinition.taskCheckConfigSettings.definitionRef.id.toLowerCase()) {
            case EvaluateArtifactPolicyCheckTypeIdString:
            case BranchProtectionPolicyCheckTypeIdString:
            case BusinessHoursPolicyCheckTypeIdString:
                return true;
        }
    }

    return false;
}

function getStatusValue(status: ApprovalStatus): number {
    switch (status) {
        case ApprovalStatus.Rejected:
        case ApprovalStatus.TimedOut:
            return 0;
        case ApprovalStatus.Pending:
            return 1;
        case ApprovalStatus.Approved:
        case ApprovalStatus.Skipped:
        case ApprovalStatus.Deferred:
            return 2;
        default:
            return 3;
    }
}

export function isCheckPermissionUpdate(permission: ChecksPermission): boolean {
    return permission === ChecksPermission.Update || permission === ChecksPermission.ResourceAdmin;
}

//ResourceTypeIcons
const Environment_IconName = "ServerEnviroment";
const Queue_IconName = "Server";
const ServiceEndPoint_IconName = "PlugDisconnected";
const VariableGroup_IconName = "AzureKeyVault";
const AgentPool_IconName = "Server";
const SecureFile_IconName = "Server";
const Repository_IconName = "GitLogo";

export const AuthorizationCheckTypeIdString: string = "E1A1FC6C-C278-4492-AE1C-C42F8697612C";
export const EvaluateArtifactPolicyCheckTypeIdString: string = "1b4fdd02-b979-480d-9a7e-0b7af7fe471f";
export const BranchProtectionPolicyCheckTypeIdString: string = "86b05a0c-73e6-4f7d-b3cf-e38f3b39a75b";
export const BusinessHoursPolicyCheckTypeIdString: string = "445fde2f-6c39-441c-807f-8a59ff2e075f";
export const ExtendsCheckTypeIdString: string = "4020E66E-B0F3-47E1-BC88-48F3CC59B5F3";
export const ExclusiveLockCheckTypeIdString: string = "2EF31AD6-BAA0-403A-8B45-2CBC9B4E5563";
export const ApprovalCheckTypeIdString: string = "8C6F20A7-A545-4486-9777-F762FAFE0D4D";
export const TaskCheckTypeIdString: string = "fe1de3ee-a436-41b4-bb20-f6eb4cb879a7";
export const ManualValidationTaskTypeId: string = "2003AD3A-104A-451A-9238-60474AB294E6";

const millisecond = 1;
const second = millisecond * 1000;
const minute = second * 60;
const hour = minute * 60;
const day = hour * 24;

//Contribution Ids for routes
const Environment_route_contribution = "ms.vss-environments-web.environment-checks-default-route";
const AdminHub_route_contribution = "ms.vss-admin-web.project-admin-hub-route";